import { useEffect } from "react";
import log from "loglevel";
import {
  alreadyReloadedFor,
  BUILD_APP_VERSION,
  fetchDeployedVersion,
  isVersionCheckEnabled,
} from "../utils/appVersion";
import { useAppVersionStore } from "../stores/appVersionStore";

// A primeira espera não compete com o boot da página.
const FIRST_CHECK_DELAY_MS = 30_000;
const CHECK_INTERVAL_MS = 5 * 60_000;

/**
 * SA-1416: pergunta de tempos em tempos (e ao voltar para a aba) qual versão
 * está no ar. Diferente da do bundle liga `updateAvailable` e para de
 * consultar. Aba escondida não consulta. Compara por diferença, não por
 * "maior": um rollback também deixa esta aba com código que saiu do ar.
 */
export function useAppVersionCheck(buildVersion: string = BUILD_APP_VERSION) {
  useEffect(() => {
    if (!isVersionCheckEnabled(buildVersion)) return;
    let disposed = false;

    const check = async () => {
      if (
        disposed ||
        useAppVersionStore.getState().updateAvailable ||
        document.visibilityState !== "visible"
      ) {
        return;
      }
      const deployed = await fetchDeployedVersion();
      if (disposed || !deployed || deployed === buildVersion) return;
      if (alreadyReloadedFor(deployed)) {
        // Recarregou e continuou no bundle antigo: avisar de novo viraria
        // reload a cada troca de seção, sem nunca chegar à versão nova.
        log.warn(
          `[app-version] já recarregou para ${deployed} e segue em ${buildVersion}; aviso suspenso`
        );
        return;
      }
      useAppVersionStore.getState().markUpdateAvailable(deployed);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") void check();
    };
    const firstTimer = window.setTimeout(
      () => void check(),
      FIRST_CHECK_DELAY_MS
    );
    const interval = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      disposed = true;
      window.clearTimeout(firstTimer);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [buildVersion]);
}
